<template>
  <div class="sc-object-card__chart-modal" v-loading="isLoading">
    <div class="sc-object-card__chart-modal__title">
      <r-icon name="camera" :size="20" />
      <r-title type="subtitle-2"> {{ $t("detector") }} </r-title>
      <r-title type="subtitle-1">
        {{ detectorId }}
      </r-title>
    </div>
    <div class="sc-object-card__chart-modal__filter">
      <r-text>
        {{ $t("filter") }}
      </r-text>
      <div
        v-for="item in filterList"
        :key="item.id"
        :class="[
          'sc-object-card__chart-modal__filter__item',
          { active: filterValue === item.value }
        ]"
        @click="setFilterValue(item.value)"
      >
        <r-text>
          {{ $t(item.value) }}
        </r-text>
      </div>
    </div>
    <div v-if="isFlexDates" class="sc-object-card__chart-modal__datepickers">
      <div class="sc-object-card__chart-modal__datepickers__item">
        <r-text>
          {{ $t("from") }}
        </r-text>
        <el-date-picker
          class="r-date-picker"
          v-model="dateFrom"
          format="dd.MM.yyyy HH:mm"
          type="datetime"
          align="right"
          placeholder="Выберите дату и время"
          :picker-options="fromPickerOptions"
          @change="datepickerChange"
        />
      </div>
      <div class="sc-object-card__chart-modal__datepickers__item">
        <r-text>
          {{ $t("to") }}
        </r-text>
        <el-date-picker
          class="r-date-picker"
          v-model="dateTo"
          format="dd.MM.yyyy HH:mm"
          type="datetime"
          align="right"
          placeholder="Выберите дату и время"
          :picker-options="toPickerOptions"
          @change="datepickerChange"
        />
      </div>
    </div>
    <div class="sc-object-card__chart-modal__chart-wrap">
      <div class="sc-object-card__chart-modal__chart-wrap-header">
        <r-icon name="bar" :size="20" />
        <r-title type="subtitle-2">
          {{ $t("chart") }}
        </r-title>
      </div>
      <div class="sc-object-card__chart-modal__chart-wrap-content">
        <chart-component
          v-if="chartData && !isLoading"
          :chartData="chartData"
          no-legend
          data-zoom
          type="type-2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import chartComponent from "@/components/chart-component/chart-component";
import { filterList } from "./configs";

export default {
  components: {
    chartComponent
  },
  props: {
    id: {
      type: String,
      required: true
    },
    data: {
      type: Object,
      required: true
    },
    type: {
      type: String,
      default: ""
    },
    cardData: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      filterList,
      filterValue: "day",
      dateTo: null,
      dateFrom: null,
      isFlexDates: false,
      chartData: null,
      fromPickerOptions: {
        disabledDate: this.disableFrom
      },
      toPickerOptions: {
        disabledDate: this.disableTo
      }
    };
  },
  watch: {
    id() {
      this.getChartData();
    }
  },
  computed: {
    detectorId() {
      return this.cardData[0] ? this.cardData[0].value : "-";
    }
  },
  created() {
    this.setDateFrom(this.filterValue);
    this.getChartData();
  },
  methods: {
    disableFrom(date) {
      if (!this.dateTo) {
        return date > new Date();
      } else {
        const toDate = new Date(this.dateTo);
        return toDate > new Date() ? date > new Date() : date > toDate;
      }
    },
    disableTo(date) {
      const fromDate = new Date(this.dateFrom);

      return date < fromDate || date > new Date();
    },
    setFilterValue(val) {
      if (val === this.filterValue) return;

      this.filterValue = val;
      this.setDateFrom(this.filterValue);
    },
    setDateFrom(value) {
      this.dateTo = Date.now();
      if (this.isFlexDates) {
        this.isFlexDates = false;
      }

      switch (value) {
        case "day":
          this.dateFrom = this.dateTo - 24 * 60 * 60 * 1000;
          this.getChartData();
          break;
        case "week":
          this.dateFrom = this.dateTo - 7 * 24 * 60 * 60 * 1000;
          this.getChartData();
          break;
        case "month":
          this.dateFrom = this.dateTo - 30 * 24 * 60 * 60 * 1000;
          this.getChartData();
          break;
        case "flex":
          this.isFlexDates = true;
          break;
      }
    },
    datepickerChange() {
      this.getChartData();
    },
    async getChartData() {
      if (!this.data || !this.data.url || !this.id) return;

      try {
        this.isLoading = true;
        const from = moment.utc(this.dateFrom).format();
        const to = moment.utc(this.dateTo).format();

        const url = `${this.data.url}&from=${from}&to=${to}&id=${this.id}`;
        const { data } = await this.$store.dispatch("REQUEST", {
          url
        });
        if (!data) {
          this.isLoading = false;
          return;
        }
        const xAxis = {
          data: Object.keys(data).map(e => moment(e).format("hh:mm"))
        };
        const fieldData = [
          {
            color: "#348D93",
            dataTitle: this.$t("intensity"),
            data: Object.values(data)
          }
        ];
        if (!this.chartData) {
          this.chartData = {};
        }
        this.chartData.xAxis = xAxis;
        this.chartData.data = fieldData;
        this.isLoading = false;
      } catch (e) {
        console.log(e);
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss">
.sc-object-card__chart-modal {
  position: relative;
  display: flex;
  flex-direction: column;

  > * {
    &:not(:first-child) {
      margin-top: 16px;
    }
  }

  &__title {
    display: flex;
    align-items: center;

    > * {
      &:not(:first-child) {
        margin-left: 4px;
      }
    }
  }

  &__filter {
    display: flex;
    align-items: center;
    padding: 8px;
    box-sizing: border-box;
    background-color: var(--bg_containers);
    width: auto;
    border-radius: var(--border-radius);

    > * {
      &:not(:first-child) {
        margin-left: 4px;
      }
    }

    &__item {
      padding: 4px 12px;
      border-radius: var(--border-radius);
      box-sizing: border-box;
      cursor: pointer;
      transition: all 0.1s ease;

      &:hover {
        background-color: var(--accent_selected);
      }

      &.active {
        background-color: var(--accent_primary_alpha_2);
        cursor: initial;
      }
    }
  }

  &__chart-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: var(--border-radius);
    background-color: var(--bg_containers);

    &-header {
      display: flex;
      align-items: center;
      background-color: var(--bg_containers);
      padding: 4px;

      > * {
        &:first-child {
          margin-right: 8px;
        }
      }

      .r-button {
        margin-left: auto;
        padding: 4px 6px !important;

        .r-icon {
          margin-right: 2px;
        }
      }
    }

    &-content {
      min-height: 50px;
      padding: 8px;
      box-sizing: border-box;
      width: 100%;
      height: 240px;
    }
  }

  &__datepickers {
    display: flex;

    > * {
      &:not(:first-child) {
        margin-left: 8px;
      }
    }

    &__item {
      .r-text {
        margin-bottom: 8px;
      }
    }
  }
}
</style>

<i18n>
{
  "ru": {
    "chart": "График",
    "intensity": "Интенсивность",
    "detector": "Детектор:",
    "filter": "Фильтровать:",
    "day": "Сутки",
    "week": "Неделя",
    "month": "Месяц",
    "flex": "Произвольный",
    "from": "Начало",
    "to": "Окончание"
  },
  "en": {
    "chart": "Chart",
    "intensity": "Intensity",
    "detector": "Detector:",
    "filter": "Filter:",
    "day": "Day",
    "week": "Week",
    "month": "Month",
    "flex": "Flex",
    "from": "From",
    "to": "To"
  }
}
</i18n>
